import React from "react";

/**
 * Skeleton loading system for the widget's help surfaces.
 *
 * Design rules (deliberate, keep them):
 * - ONE synchronized shimmer sweep across every bar (Linear/Intercom style) —
 *   per-bar offsets read as flicker, a shared phase reads as one surface.
 * - The whole skeleton FADES IN after a 120ms delay: a fetch that resolves
 *   faster than perception never flashes a skeleton at all, and a slow one
 *   eases in instead of popping.
 * - Real content then fades in over the top (handled at the usage sites), so
 *   the hand-off is a crossfade, never a hard swap.
 */

const SHIMMER = {
  background: "linear-gradient(90deg, #F2F3F5 25%, #E8EAED 37%, #F2F3F5 63%)",
  backgroundSize: "400% 100%",
  animation: "fiq-shimmer 1.4s ease infinite",
} as const;

/** Shared keyframes + delayed fade-in. Mount once at the VIEW root (not only
 *  inside the skeleton): the content fade-up runs after the skeleton unmounts,
 *  so the keyframes must outlive it. Duplicate tags are harmless. */
export const SkeletonStyles: React.FC = () => (
  <style>{`
    @keyframes fiq-shimmer { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }
    @keyframes fiq-skeleton-in { from { opacity: 0; } to { opacity: 1; } }
    @keyframes fiq-content-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
  `}</style>
);

/** One shimmering bar. Size/shape via className (h-*, w-*, rounded-*). */
export const SkeletonBar: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`rounded-md ${className || ""}`} style={SHIMMER} aria-hidden="true" />
);

/** Wrapper giving any skeleton the delayed ease-in. */
const SkeletonShell: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={className}
    style={{ animation: "fiq-skeleton-in 0.35s ease 0.12s both" }}
    aria-hidden="true"
  >
    <SkeletonStyles />
    {children}
  </div>
);

/** Class for real content replacing a skeleton — soft fade-up entrance. */
export const contentInStyle: React.CSSProperties = {
  animation: "fiq-content-in 0.3s cubic-bezier(0.16, 1, 0.3, 1) both",
};

/** Help index: collection rows (title / subtitle / count) under a count line. */
export const HelpIndexSkeleton: React.FC = () => (
  <SkeletonShell className="px-5">
    <SkeletonBar className="h-5 w-32 mt-4 mb-2" />
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="py-4 border-b border-gray-100">
        <SkeletonBar className={`h-4 mb-2 ${["w-3/5", "w-2/5", "w-3/4", "w-1/2"][i]}`} />
        <SkeletonBar className={`h-3.5 mb-2 ${["w-2/5", "w-1/2", "w-2/5", "w-1/3"][i]}`} />
        <SkeletonBar className="h-3 w-16" />
      </div>
    ))}
  </SkeletonShell>
);

/** Plain article-row list (search results / flat lists / collection pages). */
export const HelpListSkeleton: React.FC<{ rows?: number }> = ({ rows = 4 }) => (
  <SkeletonShell className="px-5">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="py-3.5 border-b border-gray-50 last:border-b-0">
        <SkeletonBar className={`h-4 mb-1.5 ${["w-3/4", "w-1/2", "w-2/3", "w-3/5", "w-1/2"][i % 5]}`} />
        <SkeletonBar className={`h-3 ${["w-1/2", "w-2/5", "w-1/3", "w-2/5", "w-1/3"][i % 5]}`} />
      </div>
    ))}
  </SkeletonShell>
);

/** Article page: title, meta line, paragraphs and an image block. */
export const ArticleSkeleton: React.FC = () => (
  <SkeletonShell>
    <SkeletonBar className="h-7 w-4/5 mt-2 rounded-lg" />
    <SkeletonBar className="h-3 w-28 mt-3 mb-6" />
    <SkeletonBar className="h-4 w-full mb-2.5" />
    <SkeletonBar className="h-4 w-11/12 mb-2.5" />
    <SkeletonBar className="h-4 w-3/5 mb-6" />
    <SkeletonBar className="h-44 w-full rounded-xl mb-6" />
    <SkeletonBar className="h-4 w-full mb-2.5" />
    <SkeletonBar className="h-4 w-4/5 mb-2.5" />
    <SkeletonBar className="h-4 w-2/3" />
  </SkeletonShell>
);
